<template>
	<div>
		<Flyout :title="titleView" :display="display" @show="close">
			<template #flyout-content>
				<div class="integrations-info">
					<div class="integrations-info__customer">
						<Dropdown
							label="Clients"
							name="ddCustomers"
							:autosuggestion="true"
							:defaultValue="'Select Client'"
							:options="customers"
							v-model="dataCustomerId"
						/>
					</div>
					<div class="integrations-info__company">
						<Dropdown
							label="Companies"
							name="ddCompanies"
							:defaultValue="'Select Company'"
							:disabled="disableddCompanies"
							:options="companies"
							v-model="dataCompanyId"
						/>
					</div>
					<div class="integrations-info__integration">
						<Dropdown
							label="DCS Integration"
							name="ddIntegrations"
							:defaultValue="'Select Integration'"
							:disabled="true"
							:options="ddIntegrationOptions"
							:value="dataSource.dcsIntegration"
						/>
					</div>
					<div class="integrations-info__dealernumber">
						<Input
							ariaLabel="Aria Label"
							className="field__input"
							label="DealerNumberId"
							name="dealerNumberId"
							:required="true"
							v-model="dataSource.dealerNumberId"
							:forceCustomErrorMessage="
								dealerNumberIdErrorMessage
							"
							@input="setDealerNumberId"
						/>
					</div>
					<div class="integrations-info__active">
						<Toggle
							:label="dataSource.active ? 'Active' : 'Inactive'"
							v-model="dataSource.active"
							:disabled="isNew"
						/>
					</div>
				</div>
			</template>
			<template #flyout-footer>
				<ButtonGroup
					:items="[
						{
							btnClick: saveAction,
							className: 'btn--primary',
							labelText: isNew ? 'Create' : 'Update',
							disabled: disableSaveButton
						},
						{
							btnClick: close,
							className: 'btn btn--link',
							labelText: 'Cancel'
						}
					]"
				/>
			</template>
		</Flyout>
		<Spinner :processor="{ loading: loading }" />
	</div>
</template>

<script>
import ButtonGroup from '@/components/forms/button/button-group';
import Dropdown from '@/components/forms/dropdown/dropdown';
import Input from '@/components/forms/input/input';
import Flyout from '@/components/shells/flyout/flyout';
import Spinner from '@/components/visual/spinner/spinner';
import Toggle from '@/components/forms/selection-control/toggle/toggle';
import ValidatePermissions from '@/sections/integration/validate-permission-mixins';
import { deepEqual } from '@/utils/object';
import { required } from 'vuelidate/lib/validators';
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';

//constants
import {
	MODULE_NAME,
	REFERENCE_ID,
	SET_INTEGRATION,
	SAVE_INTEGRATION,
	TOAST_MESSAGES_PUSH
} from '@/sections/integration/integrations-mutation-types';

import {
	MODULE_NAME as OptionsModuleName,
	SET_COMPANIES
} from '@/sections/options/options-mutation-type';

export default {
	name: 'IntegrationSettings',
	mixins: [ValidatePermissions],
	components: {
		ButtonGroup,
		Dropdown,
		Input,
		Flyout,
		Spinner,
		Toggle
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		customerId: {
			type: String,
			default: ''
		},
		display: {
			type: Boolean,
			require: true
		},
		integrations: {
			type: Object,
			require: true
		},
		thirdparty: {
			type: Object,
			require: true
		},
		source: {
			type: Object,
			default: () => {}
		}
	},
	validations: {
		dataSource: {
			dealerNumberId: {
				required
			}
		}
	},
	data() {
		return {
			confirmartionTitle: 'Integration Settings',
			dealershipGroupNumber: '',
			loading: false,
			typesId: this.thirdparty.id,
			companies: [{ text: 'All Companies (00)', value: 0 }],
			previousCompanies: [],
			dataCompanyId: 0,
			customers: [],
			dataCustomerId: '',
			initialEdition: false,
			dataSource: {},
			fieldHasErrors: false
		};
	},
	computed: {
		...mapGetters(MODULE_NAME, [REFERENCE_ID]),
		isNew() {
			return !(this.source && this.source.id > 0);
		},
		titleView() {
			if (this.isNew) {
				return 'New Integration';
			}
			return `Edit Integration - ${this.dataSource.dealerNumberId}`;
		},
		disableddCompanies() {
			return !!(!this.dataCustomerId || !this.companies.length);
		},
		disableSaveButton() {
			return (
				this.fieldHasErrors ||
				!this.pendingChanges ||
				!this.dataCustomerId ||
				!this.dataSource.dealerNumberId
			);
		},
		ddIntegrationOptions() {
			return [
				{
					text: `${this.thirdparty.dcsParty} - ${this.thirdparty.dcsIntegration}`,
					value: this.thirdparty.dcsIntegration
				}
			];
		},
		pendingChanges() {
			return !deepEqual(this.dataSource, this.source || {});
		},
		dealerNumberIdErrorMessage() {
			if (this.$v.dataSource.dealerNumberId.$dirty) {
				if (!this.$v.dataSource.dealerNumberId.required) {
					return 'DealerNumber Id is required';
				} else {
					return '';
				}
			} else {
				return '';
			}
		}
	},
	methods: {
		...mapActions(OptionsModuleName, [SET_COMPANIES]),
		close() {
			this.dataCustomerId = '';
			this.dataSource = {};
			this.$store.dispatch(`${MODULE_NAME}/${SET_INTEGRATION}`, null);
		},
		async saveAction() {
			//validate before call api
			const { id, dealerNumberId, dealershipGroupGuid } = this.dataSource;
			if ((id && this.canCreate) || (id > 0 && this.canUpdate)) {
				console.log('has permission');
			}
			const matches = this.integrations.items.find(
				x =>
					x.dealerNumberId === dealerNumberId &&
					x.dealershipGroupGuid === dealershipGroupGuid
			);
			if (matches) {
				//in the add action validate if it exists,
				//in the update action validate if there are duplicates
				if (this.isNew || (!this.isNew && matches.id !== id)) {
					this.$store.dispatch(TOAST_MESSAGES_PUSH, {
						statusCode: 400,
						message: `${dealerNumberId} already exists.`
					});
					this.fieldHasErrors = true;
					return;
				}
			}

			const payload = Object.assign({}, this.dataSource, {
				companyId: this.dataCompanyId,
				dealershipGroupGuid: this.dataCustomerId,
				partyId: this.thirdparty.partyId,
				referenceId: this[REFERENCE_ID]
			});

			await this.$store.dispatch(
				`${MODULE_NAME}/${SAVE_INTEGRATION}`,
				payload
			);
		},
		handleCustomers() {
			const { items } = this.$store.getters['options/OptionsCustomers'];

			if (items) {
				this.customers = items.map(item => {
					return {
						text: item.text,
						value: item.value,
						dealershipGroupNumber: item.description
					};
				});
			}
		},
		setDealerNumberId(value) {
			this.dataSource.dealerNumberId = value;
			this.$v.dataSource.dealerNumberId.$touch();
		},
		getDealerNumberId(companyId) {
			let group = this.dealershipGroupNumber || '';
			if (group.length < 4) {
				group = group.padStart(4, '0');
			} else {
				group = group.slice(-4);
			}
			return `SVD${group}${companyId.toString().padStart(2, '0')}`;
		}
	},
	watch: {
		dataCompanyId: {
			immediate: true,
			handler(companyId) {
				if (this.initialEdition) return;

				this.dataSource.companyId = companyId;
				if (this.isNew) {
					if (companyId) {
						this.dataSource.dealerNumberId = this.getDealerNumberId(
							companyId
						);
					} else {
						this.dataSource.dealerNumberId = this.getDealerNumberId(
							'00'
						);
					}
				}
			}
		},
		dataCustomerId: {
			immediate: true,
			handler(customerId) {
				this.dataSource.dealershipGroupGuid = customerId;
				this.dealershipGroupNumber = '';
				this.dataCompanyId = 0;

				if (customerId) {
					this.companies = [{ text: 'All Companies (00)', value: 0 }];
					const customer = this.customers.find(
						x => x.value === customerId
					);

					if (customer) {
						this.dealershipGroupNumber =
							customer.dealershipGroupNumber;

						this.loading = true;
						this[SET_COMPANIES]({
							customerId: customerId
						})
							.then(res => {
								this.previousCompanies =
									res &&
									res.items.map(x => {
										return {
											text: x.text,
											value: parseInt(x.value)
										};
									});
								this.companies = this.companies.concat(
									this.previousCompanies
								);
							})
							.catch(() => {
								this.dataCompanyId = 0;
							})
							.finally(() => {
								this.loading = false;
								if (this.initialEdition) {
									this.dataCompanyId = this.dataSource.companyId;
								}
							});
					}
				}
				if (this.isNew) {
					let company = !this.dataCompanyId
						? '00'
						: `0${this.dataCompanyId}`;
					this.dataSource.dealerNumberId = this.getDealerNumberId(
						company
					);
				}
			}
		},
		display: {
			immediate: true,
			handler(val) {
				this.initialEdition = false;
				if (val) {
					if (_.isEmpty(this.customers)) {
						this.handleCustomers();
					}

					if (this.isAuthorized(this.source || {})) {
						let data = { ...this.source } || {};

						if (data.id) {
							if (
								this.dataCustomerId &&
								this.dataCustomerId ===
									data.dealershipGroupGuid &&
								this.companies.length
							) {
								this.dataCompanyId = data.companyId;
							} else {
								this.dataCustomerId = data.dealershipGroupGuid;
								this.initialEdition = true;
							}
						} else {
							this.dataCustomerId = this.customerId || '';
							this.dataCompanyId = 0;
							data.dealerNumberId = this.getDealerNumberId('00');
							data.dcsIntegration = this.thirdparty.dcsIntegration;
							data.dcsIntegrationTypesId = this.thirdparty.id;
							data.active = true;
						}
						this.dataSource = data;
					}
				}
			}
		}
	}
};
</script>

<style lang="scss">
.integrations-info {
	> * {
		font-size: $flyout-content-font-size;
		min-width: 0;
	}

	display: grid;
	gap: $gutter-lg;
	margin-bottom: $gutter-lg;
}

/*------------------------------------*\
  #BASE MOBILE LANDSCAPE-UP
\*------------------------------------*/
@include mq($from: sm-l) {
	.integrations-info {
		grid-template-columns: 1fr 1fr;
	}
}

/*------------------------------------*\
  #BASE DESKTOP-UP
\*------------------------------------*/
@include mq($from: lg) {
	.integrations-info {
		grid-template-columns: 1fr 1fr 1fr 1fr;
	}
}
</style>
